import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, Upload, Download, CheckCircle2, AlertCircle, Loader2, ArrowRight, Sparkles, Trash2, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Conversor() {
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false); // false | kind
  const [available, setAvailable] = useState(0);
  const [justImported, setJustImported] = useState(0);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [mandantes, setMandantes] = useState([]);
  const [mandanteSel, setMandanteSel] = useState("");
  const [contratoSel, setContratoSel] = useState("");
  const [kinds, setKinds] = useState([]);
  // Postulación modal (jornada per worker)
  const [postOpen, setPostOpen] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [postWorkers, setPostWorkers] = useState([]);
  const [postAssign, setPostAssign] = useState({}); // worker_id -> jornada_id
  const [postFechaInicio, setPostFechaInicio] = useState("");
  const [postDownloading, setPostDownloading] = useState(false);

  const refreshCount = async () => {
    try { const { data } = await api.get("/exports/planilla-masiva/count"); setAvailable(data.available || 0); }
    catch (e) { /* ignore */ }
  };
  useEffect(() => {
    refreshCount();
    api.get("/mandantes").then((r) => setMandantes(r.data)).catch(() => {});
    api.get("/exports/kinds").then((r) => setKinds(r.data || [])).catch(() => setKinds([]));
  }, []);
  const contratosForSel = (mandantes.find((m) => m.name === mandanteSel)?.contratos) || [];

  const reset = () => { setFile(null); setPreview(null); };

  const handleFile = async (f) => {
    if (!f) return;
    if (!mandanteSel || !contratoSel) {
      toast.error("Selecciona Mandante y Contrato antes de subir la planilla");
      return;
    }
    setFile(f); setLoading(true); setPreview(null);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/imports/contingencia/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo leer el archivo");
      reset();
    } finally { setLoading(false); }
  };

  const executeImport = async () => {
    if (!file) return;
    if (!mandanteSel || !contratoSel) {
      toast.error("Mandante y Contrato son obligatorios");
      return;
    }
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    if (mandanteSel) fd.append("mandante", mandanteSel);
    if (contratoSel) fd.append("contrato", contratoSel);
    try {
      const { data } = await api.post("/imports/contingencia/execute", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importados ${data.new} nuevos · ${data.updated} actualizados${data.cargos_new_count ? ` · ${data.cargos_new_count} cargo(s) nuevos` : ""}`);
      setJustImported((data.new || 0) + (data.updated || 0));
      await refreshCount();
      reset();
    } catch (e) {
      toast.error("No se pudo ejecutar la importación");
    } finally { setImporting(false); }
  };

  const clearStandby = async () => {
    setClearing(true);
    try {
      const { data } = await api.delete("/exports/planilla-masiva/standby");
      toast.success(`${data.cleared ?? data.deleted} trabajador(es) retirados del standby`);
      setJustImported(0);
      await refreshCount();
    } catch (e) {
      toast.error("No se pudo vaciar el standby");
    } finally {
      setClearing(false);
      setConfirmClear(false);
    }
  };

  const exportMasiva = async () => {
    setExporting("contratistas");
    try {
      const res = await api.get("/exports/planilla-masiva", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "PlanillaMasivaContratistas.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Planilla exportada");
    } catch (e) {
      toast.error("No se pudo exportar");
    } finally { setExporting(false); }
  };

  const exportCustom = async (k) => {
    if (k.kind === "postulacion") {
      await openPostulacionModal();
      return;
    }
    setExporting(k.kind);
    try {
      const res = await api.get(`/exports/custom/${k.kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = k.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${k.title} descargada`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo exportar");
    } finally { setExporting(false); }
  };

  const openPostulacionModal = async () => {
    setPostOpen(true);
    setPostLoading(true);
    setPostWorkers([]);
    setPostAssign({});
    try {
      const { data } = await api.get("/exports/postulacion/preview");
      const assign = {};
      (data.workers || []).forEach((w) => { if (w.jornada_sugerida_id) assign[w.worker_id] = w.jornada_sugerida_id; });
      setPostWorkers(data.workers || []);
      setPostAssign(assign);
      setPostFechaInicio(data.fecha_hoy || "");
    } catch (e) {
      toast.error("No se pudo cargar la vista previa");
      setPostOpen(false);
    } finally { setPostLoading(false); }
  };

  const downloadPostulacion = async () => {
    setPostDownloading(true);
    try {
      const res = await api.post("/exports/postulacion/download",
        { assignments: postAssign, fecha_inicio: postFechaInicio },
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "PlanillaMasivaPostulacion.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Planilla masiva de Postulación descargada");
      setPostOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo exportar");
    } finally { setPostDownloading(false); }
  };

  const postPendingCount = useMemo(() => {
    return postWorkers.filter((w) => (w.jornadas?.length || 0) > 1 && !postAssign[w.worker_id]).length;
  }, [postWorkers, postAssign]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Conversor de planillas <b style={{ color: "#E35205" }}>BHP</b></h1>
        <p className="text-slate-500 mt-1">
          Carga la planilla de <b>Personal</b> y expórtala en los <b>5 formatos</b> requeridos para el proceso
          de acreditación <b>BHP</b>.
        </p>
      </div>

      <div className="grid lg:grid-cols-1 gap-6">
        {/* IMPORT */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-start gap-3 mb-4">
            <div className="bg-blue-50 text-blue-600 p-2 rounded-lg"><Upload size={18} /></div>
            <div>
              <h2 className="font-display font-semibold text-slate-800">1. Importar planilla de Personal</h2>
              <p className="text-xs text-slate-500">Sube el .xlsx operativo. Se agregarán los trabajadores al sistema.</p>
            </div>
          </div>

          {!preview ? (
            <>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Mandante <span className="text-red-500">*</span></label>
                  <select data-testid="conv-mandante-select" value={mandanteSel}
                    onChange={(e) => { setMandanteSel(e.target.value); setContratoSel(""); }}
                    className={`mt-1 w-full px-3 py-2 rounded-lg border outline-none text-sm bg-white ${mandanteSel ? "border-slate-200 focus:border-blue-400" : "border-red-200 focus:border-red-400"}`}>
                    <option value="">Selecciona un mandante…</option>
                    {mandantes.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Contrato <span className="text-red-500">*</span></label>
                  <select data-testid="conv-contrato-select" value={contratoSel}
                    onChange={(e) => setContratoSel(e.target.value)} disabled={!mandanteSel}
                    className={`mt-1 w-full px-3 py-2 rounded-lg border outline-none text-sm bg-white disabled:opacity-50 ${contratoSel ? "border-slate-200 focus:border-blue-400" : "border-red-200 focus:border-red-400"}`}>
                    <option value="">{mandanteSel ? "Selecciona un contrato…" : "Primero elige mandante"}</option>
                    {contratosForSel.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mb-3">Los trabajadores importados quedarán vinculados a este Mandante y Contrato.</p>
              <button
                data-testid="conv-dropzone"
                onClick={() => {
                  if (!mandanteSel || !contratoSel) { toast.error("Selecciona Mandante y Contrato antes de subir la planilla"); return; }
                  fileRef.current?.click();
                }}
                disabled={!mandanteSel || !contratoSel}
                className="w-full border-2 border-dashed border-slate-200 rounded-xl py-10 flex flex-col items-center gap-2 text-slate-400 hover:border-blue-300 hover:text-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-400"
              >
                {loading ? <Loader2 className="animate-spin" /> : <FileSpreadsheet size={32} />}
                <span className="text-sm font-medium">{file ? file.name : (!mandanteSel || !contratoSel) ? "Primero selecciona Mandante y Contrato" : "Haz clic para seleccionar planilla de Personal .xlsx"}</span>
              </button>
            </>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Registros" value={preview.total} />
                <Stat label="Nuevos" value={preview.new} color="text-blue-600" />
                <Stat label="Actualizados" value={preview.updated} color="text-emerald-600" />
              </div>
              <div className="max-h-56 overflow-y-auto border border-slate-100 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 sticky top-0">
                    <tr><th className="text-left p-2">RUT</th><th className="text-left p-2">Nombre</th><th className="text-left p-2">Cargo</th><th className="text-left p-2">Acción</th></tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((r, i) => (
                      <tr key={i} className="border-t border-slate-50">
                        <td className="p-2 font-mono">{r.rut || "—"}</td>
                        <td className="p-2">{r.nombre_completo}</td>
                        <td className="p-2 text-slate-500">{r.cargo || "—"}</td>
                        <td className="p-2">{r.action === "new" ? <span className="text-blue-600 flex items-center gap-1"><CheckCircle2 size={12} /> Nuevo</span> : <span className="text-emerald-600">Actualizar</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.errors?.length > 0 && (
                <div className="text-xs text-red-600 flex items-start gap-1"><AlertCircle size={13} className="mt-0.5" /> {preview.errors.length} filas con errores (RUT inválido u otro) serán omitidas.</div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={reset} className="text-sm text-slate-500 px-4 py-2">Cambiar archivo</button>
                <button data-testid="conv-import-execute" onClick={executeImport} disabled={importing}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60">
                  {importing && <Loader2 size={15} className="animate-spin" />} Confirmar importación
                </button>
              </div>
            </div>
          )}
          <input ref={fileRef} data-testid="conv-file-input" type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
        </section>

      </div>

      {/* EXPORT: 5 planillas */}
      <section data-testid="export-section" className={`bg-white rounded-2xl border p-6 shadow-sm ${available > 0 ? "border-emerald-300 ring-1 ring-emerald-200" : "border-slate-200"}`}>
        <div className="flex items-start gap-3 mb-4">
          <div className="bg-emerald-50 text-emerald-600 p-2 rounded-lg"><Download size={18} /></div>
          <div className="flex-1">
            <h2 className="font-display font-semibold text-slate-800">2. Exportar planillas</h2>
            <p className="text-xs text-slate-500">Mismo formato, colores y validaciones que las plataformas externas esperan. Los nombres de archivo se conservan EXACTOS para ser cargados en BHP / SAR / LMS.</p>
          </div>
          {available > 0 && (
            <button data-testid="conv-clear-standby" onClick={() => setConfirmClear(true)}
              className="inline-flex items-center gap-1.5 text-red-600 bg-white border border-red-200 hover:bg-red-50 text-sm font-medium px-3 py-2 rounded-lg">
              <Trash2 size={14} /> Vaciar standby
            </button>
          )}
        </div>

        {available > 0 ? (
          <div data-testid="export-ready-banner" className="mb-4 flex items-start gap-2 rounded-lg border px-3 py-2.5 bg-emerald-50 border-emerald-200">
            <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
            <div className="text-xs text-emerald-800 leading-tight">
              {justImported > 0 ? (
                <><b>¡Listo para descargar!</b> {justImported} trabajador(es) importados en esta sesión. {available} en total con datos disponibles.</>
              ) : (
                <><b>Lista para descargar.</b> {available} trabajador(es) con datos disponibles para exportar.</>
              )}
            </div>
          </div>
        ) : (
          <div className="mb-4 flex items-start gap-2 rounded-lg border px-3 py-2.5 bg-slate-50 border-slate-200">
            <AlertCircle size={16} className="text-slate-500 mt-0.5 shrink-0" />
            <div className="text-xs text-slate-600 leading-tight">Importa una planilla de Personal arriba para habilitar las descargas.</div>
          </div>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          <ExportCard
            testid="export-contratistas"
            title="Planilla masiva de Contratistas"
            filename="PlanillaMasivaContratistas.xlsx"
            description="Para crear el perfil del trabajador a la empresa contratista."
            onClick={exportMasiva}
            loading={exporting === "contratistas"}
            disabled={!!exporting || available === 0}
            available={available}
            tone="emerald"
          />
          {kinds.map((k) => (
            <ExportCard
              key={k.kind}
              testid={`export-${k.kind}`}
              title={k.title}
              filename={k.filename}
              description={k.description}
              onClick={() => exportCustom(k)}
              loading={exporting === k.kind}
              disabled={!!exporting || available === 0}
              available={available}
              tone="indigo"
            />
          ))}
        </div>
      </section>

      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Vaciar datos en standby</AlertDialogTitle>
            <AlertDialogDescription>
              Se retirarán del standby <b>{available}</b> trabajador(es) importado(s) desde la planilla de Personal. Los trabajadores, sus documentos y requisitos <b>no se eliminan</b>, solo dejarán de aparecer en la planilla pendiente de descarga.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-clear-standby" disabled={clearing} onClick={clearStandby} className="bg-red-600 hover:bg-red-700">
              {clearing ? "Vaciando…" : "Vaciar standby"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal: asignar jornada por trabajador para Planilla de Postulación */}
      <Dialog open={postOpen} onOpenChange={(o) => { if (!postDownloading) setPostOpen(o); }}>
        <DialogContent className="bg-white max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Planilla masiva de Postulación — Asignar jornada por trabajador</DialogTitle>
            <DialogDescription>
              La <b>fecha inicio</b> se asigna según la fecha de carga. La <b>fecha fin</b> viene de cada contrato.
              Si un contrato tiene más de una jornada, selecciona cuál aplicar a cada trabajador.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-3 py-2 border-b border-slate-100">
            <label className="text-xs font-medium text-slate-600">Fecha inicio asignación</label>
            <input type="date" value={postFechaInicio || ""}
              onChange={(e) => setPostFechaInicio(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-400" />
            {postPendingCount > 0 && (
              <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-md">
                <AlertCircle size={13} /> Faltan {postPendingCount} selección(es)
              </span>
            )}
          </div>

          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            {postLoading ? (
              <div className="py-12 flex flex-col items-center gap-2 text-slate-400">
                <Loader2 size={24} className="animate-spin" />
                <span className="text-sm">Cargando trabajadores…</span>
              </div>
            ) : postWorkers.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500">No hay trabajadores en standby.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-2 py-2 font-medium">Trabajador</th>
                    <th className="text-left px-2 py-2 font-medium">Contrato</th>
                    <th className="text-left px-2 py-2 font-medium">Fin contrato</th>
                    <th className="text-left px-2 py-2 font-medium">Jornada</th>
                    <th className="text-left px-2 py-2 font-medium">Resumen</th>
                  </tr>
                </thead>
                <tbody>
                  {postWorkers.map((w) => {
                    const sel = postAssign[w.worker_id] || "";
                    const j = (w.jornadas || []).find((x) => x.id === sel);
                    const ambiguous = (w.jornadas?.length || 0) > 1 && !sel;
                    const rowClass = w.no_contrato
                      ? "bg-red-50/60"
                      : w.no_jornadas
                      ? "bg-amber-50/60"
                      : ambiguous
                      ? "bg-amber-50"
                      : "";
                    return (
                      <tr key={w.worker_id} className={`border-t border-slate-100 ${rowClass}`}>
                        <td className="px-2 py-2">
                          <div className="font-medium text-slate-800">{w.name || "—"}</div>
                          <div className="font-mono text-[11px] text-slate-500">{w.rut}</div>
                        </td>
                        <td className="px-2 py-2 text-slate-600">
                          {w.contrato ? (
                            <>
                              <div>{w.contrato}</div>
                              <div className="text-[11px] text-slate-400">{w.mandante}</div>
                            </>
                          ) : <span className="italic text-red-500">Sin contrato</span>}
                        </td>
                        <td className="px-2 py-2 font-mono text-slate-700">{w.contrato_fecha_termino || "—"}</td>
                        <td className="px-2 py-2">
                          {(w.jornadas?.length || 0) === 0 ? (
                            <span className="text-[11px] italic text-amber-700">Sin jornadas en el contrato</span>
                          ) : (w.jornadas?.length || 0) === 1 ? (
                            <span className="text-slate-700">{w.jornadas[0].label}</span>
                          ) : (
                            <select data-testid={`post-jornada-${w.worker_id}`}
                              value={sel}
                              onChange={(e) => setPostAssign((p) => ({ ...p, [w.worker_id]: e.target.value }))}
                              className={`px-2 py-1 rounded border text-xs bg-white outline-none ${ambiguous ? "border-amber-400 focus:border-amber-500" : "border-slate-200 focus:border-blue-400"}`}>
                              <option value="">— Elegir —</option>
                              {w.jornadas.map((jo) => (
                                <option key={jo.id} value={jo.id}>{jo.label}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="px-2 py-2 text-slate-600 font-mono text-[11px]">
                          {j ? (
                            <>
                              <div>{j.dias_trabajo}x{j.dias_descanso} · Res. {j.numero_resolucion || "s/n"}</div>
                              <div className="text-slate-400">1er día: {j.fecha_inicio_ciclo || "—"} · Fin res.: {j.fecha_termino || "—"}</div>
                            </>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <DialogFooter className="pt-3 border-t border-slate-100">
            <button onClick={() => setPostOpen(false)} disabled={postDownloading}
              className="text-sm text-slate-600 px-4 py-2 rounded-lg hover:bg-slate-50">Cancelar</button>
            <button data-testid="post-export-btn" onClick={downloadPostulacion}
              disabled={postDownloading || postLoading || postWorkers.length === 0}
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60">
              {postDownloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {postDownloading ? "Generando…" : `Descargar (${postWorkers.length})`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, color = "text-slate-800" }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2.5 text-center border border-slate-100">
      <p className={`text-xl font-display font-bold ${color}`}>{value}</p>
      <p className="text-[11px] text-slate-400">{label}</p>
    </div>
  );
}

function ExportCard({ testid, title, filename, description, onClick, loading, disabled, available, tone = "emerald" }) {
  const toneBtn = tone === "emerald" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-indigo-600 hover:bg-indigo-700";
  const toneDot = tone === "emerald" ? "bg-emerald-500" : "bg-indigo-500";
  return (
    <div className={`rounded-xl border p-4 flex flex-col gap-3 ${available > 0 ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50"}`}>
      <div className="flex items-start gap-3">
        <span className={`h-2 w-2 rounded-full mt-2 ${toneDot}`} />
        <div className="min-w-0">
          <h3 className="font-display font-semibold text-slate-800 leading-snug">{title}</h3>
          <p className="text-xs text-slate-500 mt-0.5 leading-snug">{description}</p>
          <p className="text-[10px] font-mono text-slate-400 mt-1 truncate" title={filename}>{filename}</p>
        </div>
      </div>
      <button data-testid={testid} onClick={onClick} disabled={disabled}
        className={`mt-auto inline-flex items-center justify-center gap-2 text-white text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed ${toneBtn}`}>
        {loading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
        {loading ? "Descargando…" : `Descargar${available > 0 ? ` (${available})` : ""}`}
      </button>
    </div>
  );
}
